/**
 * Teacher Portal API
 * Fetches timetable and absence data scoped to the logged-in teacher.
 * Uses the teacher's user_id to find their teachers record, then queries accordingly.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

function getToken(): string {
  const raw = window.localStorage.getItem('supabase.auth.token');
  if (!raw) throw new Error('Not authenticated');
  return JSON.parse(raw)?.currentSession?.access_token ?? '';
}

function headers() {
  return {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${getToken()}`,
    'Content-Type': 'application/json',
  };
}

async function json<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: headers() });
  if (!res.ok) { const e = await res.json(); throw new Error(e.message ?? 'Request failed'); }
  return res.json();
}

/** Resolve teachers.id from users.id */
async function getTeacherIdForUser(userId: string): Promise<string> {
  const rows = await json<{ id: string }[]>(
    `${SUPABASE_URL}/rest/v1/teachers?user_id=eq.${userId}&select=id&limit=1`
  );
  if (!rows.length) throw new Error('No teacher record linked to this account');
  return rows[0].id;
}

// ── Today's schedule ─────────────────────────────────────────────────────────

export interface TodayPeriod {
  period_number: number;
  time_start: string;
  time_end: string;
  subject_name: string | null;
  class_name: string;
  section: string;
  period_id: string;
}

export interface TodaySchedule {
  periods: TodayPeriod[];
  activeAbsences: number;
}

export async function getTeacherTodaySchedule(userId: string): Promise<TodaySchedule> {
  const teacherId = await getTeacherIdForUser(userId);
  const today = new Date();
  const dayOfWeek = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'][today.getDay()];
  const todayStr = today.toISOString().split('T')[0];

  const [records, absences] = await Promise.all([
    json<any[]>(
      `${SUPABASE_URL}/rest/v1/timetable_records?teacher_id=eq.${teacherId}&day_of_week=eq.${dayOfWeek}&valid_to=is.null&select=*,subject:subjects(subject_name),class_section:class_sections(section,class:classes(class_name)),period:schedule_periods(period_number,time_start,time_end)&order=period_id.asc`
    ),
    json<any[]>(
      `${SUPABASE_URL}/rest/v1/absences?teacher_id=eq.${teacherId}&start_date=lte.${todayStr}&end_date=gte.${todayStr}&status=neq.cancelled&select=id`
    ),
  ]);

  const periods: TodayPeriod[] = records.map((r) => ({
    period_number: r.period?.period_number ?? 0,
    time_start: r.period?.time_start ?? '',
    time_end: r.period?.time_end ?? '',
    subject_name: r.subject?.subject_name ?? null,
    class_name: r.class_section?.class?.class_name ?? '',
    section: r.class_section?.section ?? '',
    period_id: r.period_id,
  })).sort((a, b) => a.period_number - b.period_number);

  return { periods, activeAbsences: absences.length };
}

// ── Weekly timetable ─────────────────────────────────────────────────────────

export interface WeeklyTimetable {
  records: any[];
  periods: any[];
  academicYear: string;
}

export async function getTeacherWeeklyTimetable(userId: string): Promise<WeeklyTimetable> {
  const teacherId = await getTeacherIdForUser(userId);

  // Get published academic year for the school
  const teacherRows = await json<{ school_id: string }[]>(
    `${SUPABASE_URL}/rest/v1/teachers?id=eq.${teacherId}&select=school_id`
  );
  const schoolId = teacherRows[0]?.school_id;

  const yearRows = await json<{ id: string; name: string }[]>(
    `${SUPABASE_URL}/rest/v1/academic_years?school_id=eq.${schoolId}&status=eq.published&select=id,name&limit=1`
  );
  const year = yearRows[0];

  const [records, periods] = await Promise.all([
    year
      ? json<any[]>(
          `${SUPABASE_URL}/rest/v1/timetable_records?teacher_id=eq.${teacherId}&academic_year_id=eq.${year.id}&valid_to=is.null&select=*,subject:subjects(subject_name),class_section:class_sections(section,class:classes(class_name)),period:schedule_periods(period_number,time_start,time_end)`
        )
      : Promise.resolve([]),
    schoolId
      ? json<any[]>(
          `${SUPABASE_URL}/rest/v1/schedule_periods?select=*&order=period_number.asc`
        )
      : Promise.resolve([]),
  ]);

  // Deduplicate periods (only unique period_number + time combos)
  const seen = new Set<string>();
  const uniquePeriods = periods.filter((p) => {
    const key = `${p.period_number}-${p.time_start}`;
    if (seen.has(key)) return false;
    seen.add(key); return true;
  });

  return { records, periods: uniquePeriods, academicYear: year?.name ?? '' };
}

// ── Absences ─────────────────────────────────────────────────────────────────

export async function getMyAbsences(userId: string) {
  const teacherId = await getTeacherIdForUser(userId);
  return json<any[]>(
    `${SUPABASE_URL}/rest/v1/absences?teacher_id=eq.${teacherId}&order=start_date.desc&select=*`
  );
}

export interface CreateMyAbsenceRequest {
  userId: string;
  startDate: string;
  endDate: string;
  absenceType: 'full_day' | 'temporary_duty';
  startTime?: string;
  endTime?: string;
  reason?: string;
}

export async function createMyAbsence(req: CreateMyAbsenceRequest) {
  const teacherId = await getTeacherIdForUser(req.userId);

  // Get school_id from teacher record
  const teacherRows = await json<{ school_id: string }[]>(
    `${SUPABASE_URL}/rest/v1/teachers?id=eq.${teacherId}&select=school_id`
  );
  const schoolId = teacherRows[0]?.school_id;
  if (!schoolId) throw new Error('Could not determine school');

  const body = {
    school_id: schoolId,
    teacher_id: teacherId,
    date: req.startDate,
    start_date: req.startDate,
    end_date: req.endDate,
    absence_type: req.absenceType,
    start_time: req.startTime ?? null,
    end_time: req.endTime ?? null,
    reason: req.reason ?? null,
    status: 'pending',
    created_by: req.userId,
  };

  const res = await fetch(`${SUPABASE_URL}/rest/v1/absences`, {
    method: 'POST',
    headers: { ...headers(), Prefer: 'return=representation' },
    body: JSON.stringify(body),
  });
  if (!res.ok) { const e = await res.json(); throw new Error(e.message ?? 'Failed to create absence'); }
  const data = await res.json();
  return data[0];
}

export async function cancelMyAbsence(absenceId: string) {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/absences?id=eq.${absenceId}`, {
    method: 'PATCH',
    headers: headers(),
    body: JSON.stringify({ status: 'cancelled' }),
  });
  if (!res.ok) { const e = await res.json(); throw new Error(e.message ?? 'Failed to cancel absence'); }
}
